import "./PaymentApprovals.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuthStore } from "../../../Services/authStore";
import {
  toastSuccess,
  toastError,
  toastWarning,
} from "../../../Services/toaster";

export default function PaymentApprovals() {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [approvalAction, setApprovalAction] = useState("");
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [remarksError, setRemarksError] = useState("");

  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchMyApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/payment/approvals/my-approvals`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        },
      );
      const data = response.data?.data || [];

      console.log("Payment Approvals - User ID:", user?.user_id);
      console.log("Payment Approvals - Raw data:", data);

      const mappedApprovals = data.map((item) => ({
        id: item.id,
        payment_id: item.payment_id,
        payment_number: item.payment_number,
        invoice_number: item.invoice_number,
        po_number: item.po_number,
        payment_amount: item.payment_amount,
        payment_date: item.payment_date,
        payment_method: item.payment_method,
        payment_reference: item.payment_reference || "-",
        // Using correct field names from API response
        status: item.approval_status, // Changed from item.status
        can_take_action: item.can_take_action === true,
        approval_level: `Level ${item.approval_level}`, // Format as string
        remarks: item.remarks || "",
        approver_remarks: item.approval_remarks || "", // Changed from item.approver_remarks
        payment_status: item.payment_status,
        currency: item.currency,
        created_at: item.created_at,
        updated_at: item.updated_at,
      }));

      console.log("Mapped payment approvals:", mappedApprovals);
      setApprovals(mappedApprovals);
    } catch (err) {
      console.error("Failed to load approvals:", err.response?.data);
      toastError("Failed to load approvals");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, user]);

  useEffect(() => {
    fetchMyApprovals();
  }, [fetchMyApprovals]);

  const updateApproval = async (approvalData) => {
    try {
      const userId = user?.user_id;
      console.log("Updating payment approval:", {
        id: approvalData.id,
        payment_id: approvalData.payment_id,
        status: approvalData.status,
        remarks: approvalData.remarks,
        user_id: userId,
      });

      const payload = {
        id: approvalData.id,
        payment_id: approvalData.payment_id,
        status: approvalData.status,
        remarks: approvalData.remarks,
        user_id: userId,
      };

      const response = await axios.put(
        `${API_URL}/api/payment/approvals/update`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Update approval error:", err.response?.data);
      throw err;
    }
  };

  const handleApprove = (approval) => {
    setSelectedApproval(approval);
    setApprovalAction("Approved");
    setApprovalRemarks("");
    setRemarksError("");
    setShowApprovalModal(true);
  };

  const handleReject = (approval) => {
    setSelectedApproval(approval);
    setApprovalAction("Rejected");
    setApprovalRemarks("");
    setRemarksError("");
    setShowApprovalModal(true);
  };

  const handleApprovalSubmit = async () => {
    if (!selectedApproval) return;

    if (approvalAction === "Rejected" && !approvalRemarks.trim()) {
      setRemarksError("Remarks are required when rejecting a payment");
      toastWarning("Please provide remarks for rejection");
      return;
    }

    setRemarksError("");
    setSubmitting(true);

    try {
      await updateApproval({
        id: selectedApproval.id,
        payment_id: selectedApproval.payment_id,
        status: approvalAction,
        remarks:
          approvalRemarks ||
          `${approvalAction} on ${new Date().toLocaleDateString()}`,
      });

      toastSuccess(`Payment ${approvalAction.toLowerCase()} successfully`);
      setApprovals((prev) =>
        prev.map((a) =>
          a.id === selectedApproval.id
            ? {
                ...a,
                status: approvalAction,
                approver_remarks: approvalRemarks,
              }
            : a,
        ),
      );

      setShowApprovalModal(false);
      setSelectedApproval(null);
      setApprovalRemarks("");
      setApprovalAction("");
      setRemarksError("");
      fetchMyApprovals();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(", ")
        : detail || "Failed to update approval";
      toastError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setShowApprovalModal(false);
    setSelectedApproval(null);
    setApprovalRemarks("");
    setApprovalAction("");
    setRemarksError("");
  };

  const filteredApprovals = approvals.filter((approval) => {
    const matchStatus =
      statusFilter === "All" || approval.status === statusFilter;
    const matchSearch =
      (approval.payment_number || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (approval.invoice_number || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (approval.payment_method || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (approval.approval_level || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    return matchStatus && matchSearch;
  });

  const pendingCount = approvals.filter((a) => a.status === "Pending").length;
  const approvedCount = approvals.filter((a) => a.status === "Approved").length;
  const rejectedCount = approvals.filter((a) => a.status === "Rejected").length;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusClass = (status) => {
    const map = {
      Pending: "pay-approval-status-pending",
      Approved: "pay-approval-status-approved",
      Rejected: "pay-approval-status-rejected",
    };
    return map[status] || "pay-approval-status-pending";
  };

  const getLevelClass = (level) => {
    const map = {
      "Level 1": "level-1",
      "Level 2": "level-2",
      "Level 3": "level-3",
    };
    return map[level] || "level-1";
  };

  return (
    <div className="pay-approval-page">
      <div className="pay-approval-header">
        <div className="pay-approval-header-left">
          <h1 className="pay-approval-title">Payment Approvals</h1>
          <p className="pay-approval-subtitle">
            Review and manage payment approvals
          </p>
        </div>

        <div className="pay-approval-header-right">
          <div className="pay-approval-search">
            <span className="pay-approval-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search approvals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="pay-approval-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>

          <div className="pay-approval-summary">
            <div className="pay-approval-summary-pill pay-approval-sum-pending">
              <span className="pay-approval-sum-num">{pendingCount}</span>
              <span className="pay-approval-sum-lbl">Pending</span>
            </div>
            <div className="pay-approval-summary-pill pay-approval-sum-approved">
              <span className="pay-approval-sum-num">{approvedCount}</span>
              <span className="pay-approval-sum-lbl">Approved</span>
            </div>
            <div className="pay-approval-summary-pill pay-approval-sum-rejected">
              <span className="pay-approval-sum-num">{rejectedCount}</span>
              <span className="pay-approval-sum-lbl">Rejected</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pay-approval-list">
        {loading ? (
          <div className="pay-approval-empty">
            <p>Loading approvals...</p>
          </div>
        ) : filteredApprovals.length === 0 ? (
          <div className="pay-approval-empty">
            <div className="pay-approval-empty-icon">✅</div>
            <div className="pay-approval-empty-title">All Clear!</div>
            <div className="pay-approval-empty-desc">
              {searchTerm
                ? `No approvals match "${searchTerm}"`
                : "No pending approvals at this time."}
            </div>
          </div>
        ) : (
          filteredApprovals.map((approval) => {
            const isPending = approval.status === "Pending";
            const canAct = approval.can_take_action === true;

            return (
              <div key={approval.id} className="pay-approval-card">
                <div className="pay-approval-level">
                  <span
                    className={`pay-approval-level-badge ${getLevelClass(approval.approval_level)}`}
                  >
                    {approval.approval_level}
                  </span>
                </div>

                <div className="pay-approval-content">
                  <div className="pay-approval-header-row">
                    <div>
                      <div className="pay-approval-payment-number">
                        Payment: {approval.payment_number}
                      </div>
                      <div className="pay-approval-invoice">
                        Invoice: {approval.invoice_number}
                      </div>
                      {approval.po_number && (
                        <div className="pay-approval-po">
                          PO: {approval.po_number}
                        </div>
                      )}
                    </div>
                    <span
                      className={`pay-approval-status-badge ${getStatusClass(approval.status)}`}
                    >
                      {approval.status}
                    </span>
                  </div>

                  <div className="pay-approval-details">
                    <div className="pay-approval-detail-item">
                      <span className="pay-approval-detail-label">Amount</span>
                      <span className="pay-approval-detail-value">
                        {formatCurrency(approval.payment_amount)}
                      </span>
                    </div>
                    <div className="pay-approval-detail-item">
                      <span className="pay-approval-detail-label">Date</span>
                      <span className="pay-approval-detail-value">
                        {formatDate(approval.payment_date)}
                      </span>
                    </div>
                    <div className="pay-approval-detail-item">
                      <span className="pay-approval-detail-label">Method</span>
                      <span className="pay-approval-detail-value">
                        {approval.payment_method}
                      </span>
                    </div>
                    <div className="pay-approval-detail-item">
                      <span className="pay-approval-detail-label">
                        Reference
                      </span>
                      <span className="pay-approval-detail-value">
                        {approval.payment_reference || "-"}
                      </span>
                    </div>
                    <div className="pay-approval-detail-item pay-approval-detail-full">
                      <span className="pay-approval-detail-label">Remarks</span>
                      <span className="pay-approval-detail-value">
                        {approval.remarks || "No remarks"}
                      </span>
                    </div>
                    {approval.approver_remarks && (
                      <div className="pay-approval-detail-item pay-approval-detail-full">
                        <span className="pay-approval-detail-label">
                          Approver Remarks
                        </span>
                        <span className="pay-approval-detail-value pay-approval-remarks">
                          {approval.approver_remarks}
                        </span>
                      </div>
                    )}
                  </div>

                  {isPending && canAct ? (
                    <div className="pay-approval-actions">
                      <button
                        className="pay-approval-btn pay-approval-btn-approve"
                        onClick={() => handleApprove(approval)}
                        disabled={submitting}
                      >
                        ✓ Approve
                      </button>
                      <button
                        className="pay-approval-btn pay-approval-btn-reject"
                        onClick={() => handleReject(approval)}
                        disabled={submitting}
                      >
                        ✗ Reject
                      </button>
                    </div>
                  ) : isPending && !canAct ? (
                    <div className="pay-approval-actions">
                      <div className="pay-approval-waiting">
                        ⏳ Waiting for previous level approval
                      </div>
                    </div>
                  ) : (
                    <div className="pay-approval-actions">
                      <div
                        className={`pay-approval-actioned ${approval.status === "Approved" ? "pay-approval-actioned-ok" : "pay-approval-actioned-no"}`}
                      >
                        {approval.status === "Approved"
                          ? "✓ Approved"
                          : "✗ Rejected"}
                      </div>
                      {approval.approver_remarks && (
                        <div className="pay-approval-actioned-remarks">
                          "{approval.approver_remarks}"
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {showApprovalModal && selectedApproval && (
        <div className="pay-approval-modal-overlay" onClick={handleModalClose}>
          <div
            className="pay-approval-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="pay-approval-modal-title">
              {approvalAction === "Approved" ? "Approve" : "Reject"} Payment
            </h2>
            <p className="pay-approval-modal-sub">
              {approvalAction === "Approved"
                ? "Confirm approval for this payment"
                : "Please provide a reason for rejecting this payment"}
            </p>

            <div className="pay-approval-modal-details">
              <div className="pay-approval-modal-detail">
                <span>Payment Number</span>
                <strong>{selectedApproval.payment_number}</strong>
              </div>
              <div className="pay-approval-modal-detail">
                <span>Invoice</span>
                <strong>{selectedApproval.invoice_number}</strong>
              </div>
              <div className="pay-approval-modal-detail">
                <span>Amount</span>
                <strong>
                  {formatCurrency(selectedApproval.payment_amount)}
                </strong>
              </div>
              <div className="pay-approval-modal-detail">
                <span>Approval Level</span>
                <strong>{selectedApproval.approval_level}</strong>
              </div>
            </div>

            <div className="pay-approval-modal-remarks">
              <label>
                Remarks{" "}
                {approvalAction === "Rejected" && (
                  <span className="pay-approval-required">*</span>
                )}
                {approvalAction === "Rejected" && (
                  <span className="pay-approval-required-text">
                    {" "}
                    (Required for rejection)
                  </span>
                )}
              </label>
              <textarea
                rows="3"
                value={approvalRemarks}
                onChange={(e) => {
                  setApprovalRemarks(e.target.value);
                  if (remarksError) setRemarksError("");
                }}
                placeholder={
                  approvalAction === "Rejected"
                    ? "Please provide a reason for rejection..."
                    : "Add remarks (optional)..."
                }
                className={remarksError ? "pay-approval-textarea-error" : ""}
              />
              {remarksError && (
                <div className="pay-approval-error-message">{remarksError}</div>
              )}
            </div>

            <div className="pay-approval-modal-actions">
              <button
                className="pay-approval-btn pay-approval-btn-secondary"
                onClick={handleModalClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className={`pay-approval-btn ${approvalAction === "Approved" ? "pay-approval-btn-approve" : "pay-approval-btn-reject"}`}
                onClick={handleApprovalSubmit}
                disabled={submitting}
              >
                {submitting
                  ? "Processing..."
                  : approvalAction === "Approved"
                    ? "✓ Approve"
                    : "✗ Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
